import { useEffect, useRef, useState } from 'react'
import type { ValidationIssue } from '../api/types.js'
import type { Graph } from './graph.js'

export interface ValidationClient {
  validateGraph(pipelineId: string, graph: unknown): Promise<ValidationIssue[]>
}

export interface UseValidationResult {
  issues: readonly ValidationIssue[]
  /** Отказ самой проверки: сеть или права, а не замечание к графу. */
  error: string | null
}

export const DEFAULT_VALIDATION_DELAY_MS = 300

/** Замечания по узлам: сколько их у каждого. Узлу нужно только число. */
export function issuesByNode(issues: readonly ValidationIssue[]): Record<string, number> {
  const counted: Record<string, number> = {}
  for (const issue of issues) {
    if (issue.nodeId === null) continue
    counted[issue.nodeId] = (counted[issue.nodeId] ?? 0) + 1
  }
  return counted
}

/** Замечания про граф целиком: показать их на узле нельзя. */
export function graphIssues(issues: readonly ValidationIssue[]): ValidationIssue[] {
  return issues.filter((issue) => issue.nodeId === null)
}

/**
 * Проверка графа по мере редактирования (FR-UI-001). Проверяет служба тем
 * же валидатором, что и публикация (FR-PIPE-004): вторая реализация на
 * клиенте разошлась бы с первой в первый же месяц — и редактор обещал бы
 * публикацию, которую служба отклонит.
 *
 * Проверка отложена: запрос на каждое нажатие клавиши значит десятки
 * проверок в секунду.
 */
export function useValidation(
  /** `null` — проверять нечем: редактор открыт без службы (тесты панелей). */
  client: ValidationClient | null,
  pipelineId: string | null,
  graph: Graph,
  delayMs: number = DEFAULT_VALIDATION_DELAY_MS,
): UseValidationResult {
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([])
  const [error, setError] = useState<string | null>(null)
  // Номер последней начатой проверки. Ответ более раннего запроса, пришедший
  // после более позднего, — это замечания к графу, которого уже нет.
  const latest = useRef(0)
  // Клиент держится ссылкой и в зависимости не входит: вызывающий передаёт
  // его литералом, и на каждую перерисовку получался бы новый объект — то
  // есть новая проверка на каждый ответ предыдущей, без конца.
  const current = useRef(client)
  current.current = client

  useEffect(() => {
    if (pipelineId === null) return undefined

    const attempt = latest.current + 1
    latest.current = attempt

    const timer = setTimeout(() => {
      const target = current.current
      if (target === null) return
      target
        .validateGraph(pipelineId, graph)
        .then((found) => {
          if (latest.current !== attempt) return
          setIssues(found)
          setError(null)
        })
        .catch((failure: unknown) => {
          if (latest.current !== attempt) return
          // Молчаливый отказ проверки — худший исход: человек правит граф,
          // видит чистый холст и думает, что замечаний нет.
          setError(failure instanceof Error ? failure.message : 'Проверка не удалась.')
        })
    }, delayMs)

    return () => {
      clearTimeout(timer)
    }
  }, [pipelineId, graph, delayMs])

  return { issues, error }
}
